import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV } from './nav';

interface Analytics {
    total_tickets_30d: number; resolved_30d: number; resolution_rate_pct: number;
    avg_first_reply_hours: number | null;
    by_category: { category: string; count: number }[];
    by_priority: { priority: string; count: number }[];
    note: string;
}

const ResponseAnalytics: React.FC = () => {
    const { api } = useInternalApi();
    const [data, setData] = useState<Analytics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Response Analytics - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/support/response-time-analytics/').then(res => {
            if (res.data?.status === 'success') setData(res.data.data);
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faChartBar} className="me-2 text-primary" />Response Analytics</h2>
            <p className="text-muted mb-4">Response time and resolution performance over the last 30 days</p>

            {loading || !data ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="alert alert-info small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{data.note}</div>

                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Tickets (30d)</div><h4>{data.total_tickets_30d}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Resolved</div><h4>{data.resolved_30d}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Resolution Rate</div><h4>{data.resolution_rate_pct}%</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Avg First Reply</div>
                                <h4>{data.avg_first_reply_hours !== null ? `${data.avg_first_reply_hours}h` : '—'}</h4>
                            </div></div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>By Category</strong></div>
                                <div className="card-body p-0">
                                    {data.by_category.length === 0 ? (
                                        <div className="text-center py-4 text-muted">No tickets in range.</div>
                                    ) : (
                                        <table className="table table-sm mb-0"><tbody>
                                            {data.by_category.map(c => (
                                                <tr key={c.category}><td>{c.category}</td><td className="text-end"><span className="badge bg-secondary">{c.count}</span></td></tr>
                                            ))}
                                        </tbody></table>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>By Priority</strong></div>
                                <div className="card-body p-0">
                                    {data.by_priority.length === 0 ? (
                                        <div className="text-center py-4 text-muted">No tickets in range.</div>
                                    ) : (
                                        <table className="table table-sm mb-0"><tbody>
                                            {data.by_priority.map(p => (
                                                <tr key={p.priority}><td>{p.priority}</td><td className="text-end"><span className="badge bg-secondary">{p.count}</span></td></tr>
                                            ))}
                                        </tbody></table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default ResponseAnalytics;
